import React from 'react';
import { Box, Container, Grid, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import HealthAndSafetyRoundedIcon from '@mui/icons-material/HealthAndSafetyRounded';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import PageLayout from '../components/PageLayout';
import Section, { SectionHeading } from '../components/PageSection';
import { FeatureCard, FeatureIcon, CardTitle, CardText, accentAt, AccreditationNote } from '../components/ui';

const SITE = 'https://qasc.it';

// The three featured courses of the original /corsi-in-evidenza page, each
// with the content of its original detail page.
const courses = [
  {
    icon: <GroupsRoundedIcon />,
    title: 'Formazione generale dei lavoratori',
    duration: 'Durata: 4 ore',
    paragraphs: [
      'La formazione dei lavoratori sulla sicurezza è uno dei principali obblighi a carico del Datore di Lavoro, così come previsto in diversi articoli del D.Lgs. 81/08. Primo fra tutti, l’art. 37 prevede che il Datore di Lavoro provveda alla formazione dei lavoratori secondo i contenuti e la durata previsti dall’Accordo Stato-Regioni del 21/12/2011.',
      'Il personale di nuova assunzione deve essere avviato ai rispettivi corsi di formazione anteriormente o, se ciò non risulta possibile, contestualmente all’assunzione. Il relativo percorso formativo deve essere completato entro e non oltre 60 giorni dall’assunzione, salvo particolari eccezioni specificate nel decreto.',
    ],
  },
  {
    icon: <HealthAndSafetyRoundedIcon />,
    title: 'Formazione specifica — rischio basso, medio e alto',
    duration: 'Durata: 16 / 32 / 48 ore per il Datore di Lavoro RSPP',
    paragraphs: [
      'In alcuni casi il Datore di Lavoro può svolgere direttamente il ruolo di RSPP (D.Lgs. 81/08, art. 34 e allegato II Accordo Stato-Regioni 21/11/2011): aziende artigiane e industriali fino a 30 lavoratori, aziende agricole e zootecniche fino a 30 lavoratori, aziende della pesca fino a 20 lavoratori, altre aziende fino a 200 lavoratori.',
      'Il Datore di Lavoro che intende svolgere i compiti di RSPP ha l’obbligo di seguire un corso di formazione della durata minima di 16 ore e massima di 48 ore, in funzione della natura dei rischi presenti sul luogo di lavoro e delle attività lavorative svolte: azienda a rischio basso 16 ore, rischio medio 32 ore, rischio alto 48 ore.',
    ],
  },
  {
    icon: <RecordVoiceOverRoundedIcon />,
    title: 'Formazione RLS',
    duration: 'Durata: 32 ore',
    paragraphs: [
      'Il Rappresentante dei lavoratori per la sicurezza (RLS) è la “persona eletta o designata per rappresentare i lavoratori per quanto concerne gli aspetti della salute e della sicurezza durante il lavoro”. La figura dell’RLS deve essere un punto di riferimento per tutti i lavoratori: deve monitorare che la gestione della sicurezza sul posto di lavoro avvenga nel pieno rispetto delle norme stabilite dal Testo Unico.',
      'La nomina, definita dall’art. 47 del D.Lgs. 81/08, avviene da un’assemblea dei lavoratori tra i dipendenti assunti a tempo indeterminato; non possono candidarsi i lavoratori in prova, gli apprendisti e chi ha un contratto a tempo determinato o di formazione professionale. Gli RLS non possono essere nominati RSPP o ASPP.',
      'La formazione per RLS prevede un aggiornamento obbligatorio che non può essere inferiore a 4 ore annue per le imprese che occupano fino a 50 lavoratori e a 8 ore annue per le imprese che occupano oltre i 50 lavoratori.',
    ],
  },
];

function CorsiEvidenzaPage() {
  return (
    <PageLayout
      seo={{
        title: 'Corsi in Evidenza | QAS Consulting',
        description:
          'I corsi in evidenza di QAS Consulting: formazione generale dei lavoratori, formazione specifica per rischio basso, medio e alto e formazione RLS.',
        canonical: `${SITE}/corsi-in-evidenza`,
      }}
      hero={{
        kicker: 'Formazione',
        title: 'Corsi in evidenza',
        subtitle: 'I percorsi che rispondono agli obblighi formativi più diffusi a carico del Datore di Lavoro.',
      }}
    >
      <Section tone="light">
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {courses.map((c, i) => (
              <Grid item xs={12} key={c.title}>
                <FeatureCard sx={{ p: { xs: '28px 22px', md: '40px' } }}>
                  <Grid container spacing={4}>
                    <Grid item xs={12} md={4}>
                      <FeatureIcon accent={accentAt(i)}>{c.icon}</FeatureIcon>
                      <CardTitle sx={{ fontSize: '21px', mb: 1 }}>{c.title}</CardTitle>
                      <Typography
                        sx={{
                          display: 'inline-block',
                          fontFamily: '"Sora", sans-serif',
                          fontSize: '13px',
                          fontWeight: 700,
                          color: '#00425c',
                          backgroundColor: 'rgba(0,66,92,0.08)',
                          borderRadius: '999px',
                          px: '14px',
                          py: '5px',
                        }}
                      >
                        {c.duration}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      {c.paragraphs.map((p) => (
                        <CardText key={p.slice(0, 40)} sx={{ fontSize: '15px', mb: 2 }}>
                          {p}
                        </CardText>
                      ))}
                      <Typography
                        component={RouterLink}
                        to="/contatti"
                        sx={{
                          fontFamily: '"Sora", sans-serif',
                          fontWeight: 600,
                          fontSize: '14.5px',
                          color: 'primary.main',
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        Contattaci per ulteriori informazioni →
                      </Typography>
                    </Grid>
                  </Grid>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
          <AccreditationNote />
        </Container>
      </Section>
    </PageLayout>
  );
}

export default CorsiEvidenzaPage;
